import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, XCircle } from "lucide-react";
import { api } from "../../lib/api";
import { apiErrorMessage } from "../../lib/errors";
import SportTabs from "../../components/terminal/SportTabs";
import {
  Cell,
  EmptyState,
  HeadCell,
  MatchupCell,
  StartTimeCell,
  dqTone,
} from "../../components/terminal/DataTable";

export default function NoBet() {
  const [rows, setRows] = useState(null);
  const [sport, setSport] = useState("");

  const load = useCallback(async (s) => {
    try {
      const params = s ? { sport: s } : {};
      const { data } = await api.get("/terminal/no-bet", { params });
      setRows(data.rows || []);
    } catch (err) {
      console.warn("no-bet load failed", err);
      toast.error(apiErrorMessage(err, "Could not load no-bet list"));
      setRows([]);
    }
  }, []);

  useEffect(() => { load(sport); }, [sport, load]);

  return (
    <div data-testid="no-bet-page">
      <div className="mb-4">
        <h2 className="font-display text-2xl flex items-center gap-2">
          <XCircle size={20} className="text-red-400" />
          No-bet analyzer
        </h2>
        <p className="mono text-[11px] text-zinc-500 mt-1">
          Games the AI explicitly declines — low data quality, heavy-favorite line,
          unknown starter, volatility flag, or the model's variance too wide for
          Martingale step 1.
        </p>
      </div>

      <SportTabs value={sport} onChange={setSport} testidPrefix="no-bet-sport" />

      {rows === null && (
        <div className="py-8 text-center mono text-xs text-zinc-500">
          <Loader2 size={16} className="inline animate-spin mr-2" />
          Loading…
        </div>
      )}
      {rows && rows.length === 0 && (
        <EmptyState
          testid="no-bet-empty"
          message="Nothing on the current slate is flagged AVOID."
        />
      )}

      {rows && rows.length > 0 && (
        <div className="border border-zinc-800 overflow-x-auto" data-testid="no-bet-table">
          <table className="w-full min-w-[900px]">
            <thead>
              <tr>
                <HeadCell>START</HeadCell>
                <HeadCell>MATCHUP</HeadCell>
                <HeadCell>WHY SKIP</HeadCell>
                <HeadCell className="text-right">DQ</HeadCell>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.key} className="hover:bg-zinc-900/40" data-testid={`no-bet-row-${r.key}`}>
                  <Cell><StartTimeCell iso={r.start_time} /></Cell>
                  <Cell><MatchupCell row={r} /></Cell>
                  <Cell className="text-zinc-300">
                    {(r.no_bet_reasons || []).length > 0 ? (
                      <ul className="space-y-0.5">
                        {r.no_bet_reasons.map((reason) => (
                          <li key={reason} className="flex gap-1.5">
                            <span className="text-red-400">!</span>
                            <span>{reason}</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <span className="text-zinc-500">Not viable for step-1 Martingale</span>
                    )}
                  </Cell>
                  <Cell className={`text-right ${dqTone(r.data_quality_score)}`}>
                    {r.data_quality_score ?? 0}
                  </Cell>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
